import { DmsModal, DmsButton, intl, fetchData } from 'dmsbasereact';
import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, Paper, CircularProgress } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { getGenderEnum } from '../../constants/GenderEnum';
import { formatCpf, formatCep } from '../../utils/formatters';

const PatientPreviewModal = ({
  isVisible,
  onClose,
  onConfirm,
  patientData = null,
  responsibleLegalData = null,
  isLoading = false
}) => {
    // Adicione estados para armazenar os métodos contraceptivos
    const [methodLabels, setMethodLabels] = useState({});

    // Buscar métodos contraceptivos para mostrar os labels corretos
    useEffect(() => {
        const fetchMethodLabels = async () => {
            try {
                const response = await fetchData('/contraceptive_method/all', 'GET', true);
                if (response?.data) {
                    // Handle both response.data and response.data.data formats
                    const methods = response.data.data || response.data;
                    const labelsMap = {};

                    // Ensure methods is always an array
                    const methodsArray = Array.isArray(methods) ? methods : [];

                    methodsArray.forEach(method => {
                        labelsMap[method.pk] = method.reason;
                    });

                    setMethodLabels(labelsMap);
                }
            } catch (error) {
                console.error('Error fetching contraceptive methods for labels:', error);
            }
        };

        if (isVisible && patientData) {
            fetchMethodLabels();
        }
    }, [isVisible, patientData]);

    if (!patientData) {
        return null;
    }

    const SectionHeader = ({ icon: Icon, title, color = 'primary.main' }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box sx={{
                p: 1,
                borderRadius: 1,
                bgcolor: `${color.split('.')[0]}.50`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <Icon sx={{ color, fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {title}
            </Typography>
        </Box>
    );

    const DataRow = ({ label, value, fullWidth = false }) => (
        <Grid item xs={12} sm={fullWidth ? 12 : 6}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {label}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {value || '-'}
            </Typography>
        </Grid>
    );

    const formatPhone = (phone) => {
        if (!phone) return '-';
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 11) {
            return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        }
        if (cleaned.length === 10) {
            return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
        }
        return phone;
    };

    const getGenderText = (gender) => {
        if (gender === getGenderEnum().M.value) {
            return intl.get("Male").d("Male");
        }
        if (gender === getGenderEnum().F.value) {
            return intl.get("Female").d("Female");
        }
        return '-';
    };

    // Função atualizada para usar os labels reais
    const getMethodLabel = (methodValue) => {
        return methodLabels[methodValue] || methodValue;
    };

    const isFemale = patientData.gender === getGenderEnum().F.value;

    return (
        <DmsModal isVisible={isVisible} onClose={onClose} maxWidth="md" fullWidth>
            <DmsModal.Header
                title={intl.get("PatientPreview").d("Revisar Dados do Paciente")}
                onClose={onClose}
            />
            <DmsModal.Body>
                <Box sx={{ py: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {intl.get("PatientPreviewSubtitle").d("Revise todos os dados antes de cadastrar o paciente")}
                    </Typography>

                    {/* Patient Data Section */}
                    <Paper elevation={0} sx={{ p: 2.5, mb: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.100', borderRadius: 2 }}>
                        <SectionHeader icon={PersonIcon} title={intl.get("PatientData").d("Patient Data")} color="primary.main" />
                        <Grid container spacing={2}>
                            <DataRow label={intl.get("Name").d("Name")} value={patientData.name} fullWidth />
                            <DataRow label={intl.get("CPF").d("CPF")} value={formatCpf(patientData.cpf)} />
                            <DataRow
                                label={intl.get("BirthDate").d("Birth Date")}
                                value={patientData.birthDate ? dayjs(patientData.birthDate).format('DD/MM/YYYY') : '-'}
                            />
                            <DataRow label={intl.get("Gender").d("Gender")} value={getGenderText(patientData.gender)} />
                            {patientData.rg && (
                                <DataRow label={intl.get("RG").d("RG")} value={patientData.rg} />
                            )}
                            {patientData.rgIssuingAgency && (
                                <DataRow label={intl.get("IssuingAgency").d("Issuing Agency")} value={patientData.rgIssuingAgency} />
                            )}
                            <DataRow label={intl.get("Phone").d("Phone")} value={formatPhone(patientData.phone)} />
                            {patientData.email && (
                                <DataRow label={intl.get("Email").d("Email")} value={patientData.email} />
                            )}
                        </Grid>
                    </Paper>

                    {/* Address Section */}
                    <Paper elevation={0} sx={{ p: 2.5, mb: 2, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.100', borderRadius: 2 }}>
                        <SectionHeader icon={HomeIcon} title={intl.get("AddressInfo").d("Address Information")} color="info.main" />
                        <Grid container spacing={2}>
                            {patientData.zipCode && (
                                <DataRow label={intl.get("CEP").d("Zip Code")} value={formatCep(patientData.zipCode)} />
                            )}
                            {patientData.city && (
                                <DataRow label={intl.get("City").d("City")} value={patientData.city} />
                            )}
                            {patientData.state && (
                                <DataRow label={intl.get("State").d("State")} value={patientData.state} />
                            )}
                            {patientData.address && (
                                <DataRow label={intl.get("Address").d("Address")} value={patientData.address} />
                            )}
                            {patientData.addressNumber && (
                                <DataRow label={intl.get("Number").d("Number")} value={patientData.addressNumber} />
                            )}
                        </Grid>
                    </Paper>

                    {/* Medical Information Section - Only for female patients */}
                    {isFemale && (
                        <Paper elevation={0} sx={{ p: 2.5, mb: 2, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.100', borderRadius: 2 }}>
                            <SectionHeader icon={LocalHospitalIcon} title={intl.get("MedicalInfo").d("Medical Information")} color="warning.main" />
                            <Grid container spacing={2}>
                                <DataRow
                                    label={intl.get("FertilityPotencial").d("Fertility Potential")}
                                    value={patientData.hasPregnancyPotential
                                        ? intl.get("Yes").d("Yes")
                                        : intl.get("No").d("No")}
                                />
                                {patientData.hasPregnancyPotential && (
                                    <DataRow
                                        label={intl.get("MenstrualCycleRegular").d("Menstrual Cycle Regular")}
                                        value={patientData.menstrualCycleRegular
                                            ? intl.get("Yes").d("Yes")
                                            : intl.get("No").d("No")}
                                    />
                                )}
{patientData.hasPregnancyPotential && patientData.highlyEffectiveMethods && patientData.highlyEffectiveMethods.length > 0 && (
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                            {intl.get("HighlyEffectiveMethods").d("Highly Effective Method")}
                                        </Typography>
                                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                            {patientData.highlyEffectiveMethods.map((methodPk, idx) => (
                                                <Typography key={idx} component="li" variant="body2" sx={{ fontWeight: 500 }}>
                                                    {getMethodLabel(methodPk)}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Grid>
                                )}
                                {patientData.hasPregnancyPotential && patientData.barrierMethods && patientData.barrierMethods.length > 0 && (
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                            {intl.get("BarrierMethods").d("Barrier Method")}
                                        </Typography>
                                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                            {patientData.barrierMethods.map((methodPk, idx) => (
                                                <Typography key={idx} component="li" variant="body2" sx={{ fontWeight: 500 }}>
                                                    {getMethodLabel(methodPk)}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Grid>
                                )}
                                {!patientData.hasPregnancyPotential && patientData.infertilityReasonName && (
                                    <DataRow
                                        label={intl.get("InfertilityReason").d("Infertility Reason")}
                                        value={patientData.infertilityReasonName}
                                        fullWidth
                                    />
                                )}
                            </Grid>
                        </Paper>
                    )}

                    {/* Responsible Legal Section */}
                    {responsibleLegalData && (
                        <Paper elevation={0} sx={{ p: 2.5, mb: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.100', borderRadius: 2 }}>
                            <SectionHeader icon={SupervisorAccountIcon} title={intl.get("ResponsibleLegal").d("Legal Guardian")} color="success.main" />
                            <Grid container spacing={2}>
                                <DataRow label={intl.get("Name").d("Name")} value={responsibleLegalData.name} fullWidth />
                                <DataRow label={intl.get("CPF").d("CPF")} value={formatCpf(responsibleLegalData.cpf)} />
                                {responsibleLegalData.birthDate && (
                                    <DataRow
                                        label={intl.get("BirthDate").d("Birth Date")}
                                        value={dayjs(responsibleLegalData.birthDate).format('DD/MM/YYYY')}
                                    />
                                )}
                                {responsibleLegalData.phone && (
                                    <DataRow label={intl.get("Phone").d("Phone")} value={formatPhone(responsibleLegalData.phone)} />
                                )}
                                {responsibleLegalData.email && (
                                    <DataRow label={intl.get("Email").d("Email")} value={responsibleLegalData.email} />
                                )}
                            </Grid>

                            {/* Responsible Legal Address */}
                            {(responsibleLegalData.zipCode || responsibleLegalData.city || responsibleLegalData.address) && (
                                <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'success.200' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.dark', display: 'block', mb: 1 }}>
                                        {intl.get("AddressInfo").d("Address Information")}
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {responsibleLegalData.zipCode && (
                                            <DataRow label={intl.get("CEP").d("Zip Code")} value={formatCep(responsibleLegalData.zipCode)} />
                                        )}
                                        {responsibleLegalData.city && (
                                            <DataRow label={intl.get("City").d("City")} value={responsibleLegalData.city} />
                                        )}
                                        {responsibleLegalData.state && (
                                            <DataRow label={intl.get("State").d("State")} value={responsibleLegalData.state} />
                                        )}
                                        {responsibleLegalData.address && (
                                            <DataRow label={intl.get("Address").d("Address")} value={responsibleLegalData.address} />
                                        )}
                                        {responsibleLegalData.addressNumber && (
                                            <DataRow label={intl.get("Number").d("Number")} value={responsibleLegalData.addressNumber} />
                                        )}
                                    </Grid>
                                </Box>
                            )}
                        </Paper>
                    )}
                </Box>
            </DmsModal.Body>
            <DmsModal.Footer>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, width: '100%' }}>
                    <DmsButton
                        variant="secondary"
                        text={intl.get("BackToEdit").d("Back to Edit")}
                        onClick={onClose}
                        disabled={isLoading}
                    />
                    <DmsButton
                        variant="primary"
                        text={isLoading
                            ? intl.get("RegisteringPatient").d("Cadastrando...")
                            : intl.get("ConfirmRegistration").d("Confirmar Cadastro")
                        }
                        onClick={onConfirm}
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : null}
                    />
                </Box>
            </DmsModal.Footer>
        </DmsModal>
    );
};

PatientPreviewModal.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    patientData: PropTypes.shape({
        name: PropTypes.string,
        cpf: PropTypes.string,
        birthDate: PropTypes.object,
        gender: PropTypes.string,
        rg: PropTypes.string,
        rgIssuingAgency: PropTypes.string,
        phone: PropTypes.string,
        email: PropTypes.string,
        zipCode: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        address: PropTypes.string,
        addressNumber: PropTypes.string,
        hasPregnancyPotential: PropTypes.bool,
        menstrualCycleRegular: PropTypes.bool,
        highlyEffectiveMethods: PropTypes.arrayOf(PropTypes.number),
        barrierMethods: PropTypes.arrayOf(PropTypes.number),
        infertilityReasonName: PropTypes.string,
    }),
    responsibleLegalData: PropTypes.shape({
        name: PropTypes.string,
        cpf: PropTypes.string,
        birthDate: PropTypes.object,
        phone: PropTypes.string,
        email: PropTypes.string,
        zipCode: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        address: PropTypes.string,
        addressNumber: PropTypes.string,
    }),
    isLoading: PropTypes.bool
};

export default PatientPreviewModal;